import {Controller} from "@hotwired/stimulus";
import {useClickOutside} from "stimulus-use";
import {bodyOverflowAuto, bodyOverflowHidden} from "./mixins/process_body_overflow.js";
import {useZoomListener} from "./mixins/use_zoom_listener.js";
import {useResizeListener} from "./mixins/use_resize_listener";
import {useTeleportToBody} from "./mixins/teleport_to_body.js";

/**
 * Dropdown controller allows to open and close a drop down menu on a button click.
 * Drop down menu can be closed on clicking outside as well.
 *
 *
 *  %nav{data: { controller: 'dropdown',
 *               dropdown_opened_class: 'flex',
 *               dropdown_closed_class: 'hidden' }}
 *    %button{aria: { expanded: 'false', label: 'Menu' },
 *            data: { dropdown_target: 'toggler', action: 'click->dropdown#toggle'} }
 *
 *    %ul{data: { dropdown_target: 'content'}}
 *      %li Menu item 1
 *      %li Menu item 2
 *      %li Menu item 3
 *
 */
export default class extends Controller {
  static targets = [
    'toggler',
    'content',
  ];

  static classes = ['opened', 'closed'];

  /** preferredPosition and offset values allows us
   * to pass preferred position and offset
   * to the DropdownPositioner.
   * See app/frontend/javascript/controllers/mixins/use_dropdown_positioner.js
   *
   * Use the useHiddenAttribute `false` value for submenus
   * where we do not want to hide these submenus using
   * the hidden attribute when collapsed.
   *
   * A typical example is the main/primary menu,
   * where we do not want to hide its individual submenus in the collapsed variant
   * using the hidden attribute, but we want to control visibility directly
   * using the TW collapsed variant so that
   * they are visible when this variant is used.
   *
   * In other words: it is easy to use the collapsed TW variant
   * because we can use it in the form of TW selectors.
   * On the other hand, however, it is impossible to control
   * the hidden attribute using TW or CSS classes.
   */
  static values = {
    preferredPosition: {type: String, default: 'bottom-end'},
    offset: {type: Number, default: 0},
    useHiddenAttribute: {type: Boolean, default: true}
  }

  connect() {
    super.connect();
    useClickOutside(this);
    useZoomListener(this);
    useResizeListener(this);
    useTeleportToBody(this);

    if (this.responsiveTogglerValue) this._setUpResponsiveObserver();

    this.clickOutsideListener = this._clickOutsideListener.bind(this);

    this._setupTogglersObserverAndState();
  }

  disconnect() {
    super.disconnect();
    this._removeClickOutsideListener();
    if (this.observer) this.observer.disconnect();
  }

  togglerIsHiddenByCss(toggler) {
    return window.getComputedStyle(toggler)['display'] === 'none';
  }

  toggle(event) {
    if (event.currentTarget.ariaExpanded === 'true') {
      this._removeClickOutsideListener();
      this.hideDropdown(event.currentTarget);
    } else {
      this._addClickOutsideListener();
      this.showDropdown(event.currentTarget);
    }
  }

  /**
   * This method shows the dropdown content by setting the toggler's
   * ariaExpanded to true, showing the content, adding the opened class, and
   * removing the closed class.
   */
  showDropdown(toggler) {
    toggler.ariaExpanded = true;

    const content = this.hasContentTarget ? this.contentTarget : null;
    if (!content) return;

    content.classList.add('invisible');
    content.hidden = false;
    content.classList.add(this.openedClass);
    content.classList.remove(this.closedClass);

    if (!this._positioner && this.hasTogglerTarget) {
      import('./mixins/use_dropdown_positioner').then(({DropdownPositioner}) => {
        this._positioner = new DropdownPositioner(this.togglerTarget, content,
          {
            placement: this.preferredPositionValue,
            verticalCompensation: 10,
            offsetPx: this.offsetValue,
          });
        this._positioner.init();
      });
    }

    bodyOverflowHidden(this)

    if (typeof content.show === 'function') {
      content.show();
    }

    /** Teleport content to body so it escapes stacking/overflow contexts.  */
    this.teleportElementToBody(content);
  }

  hideDropdown(toggler) {
    /** Use cached teleported element when stimulus target is no longer available. */
    const teleported = this.getTeleportedElement();

    const dropdown = teleported || (this.hasContentTarget ? this.contentTarget : null);
    if (dropdown && !this.togglerIsHiddenByCss(toggler)) {
      toggler.ariaExpanded = false;
      if (this.useHiddenAttributeValue) {
        dropdown.hidden = true;
      }
      dropdown.classList.remove(this.openedClass);
      dropdown.classList.add(this.closedClass);

      if (this._positioner) {
        this._positioner.destroy();
        this._positioner = null;
      }
      Object.assign(dropdown.style, {
        position: '',
        top: '',
        left: '',
        right: '',
        height: '',
        zIndex: '',
        maxHeight: '',
        overflow: '',
      });

      /** Restore teleported element back to original parent if needed. */
      if (teleported) {
        this.restoreTeleportedElement();
      }

      if (typeof dropdown.remove === 'function' && toggler.dataset && toggler.dataset['value'] === 'context') {
        dropdown.remove();
      }
      bodyOverflowAuto(this);
    }
  }

  _clickOutsideListener(event) {
    if (event.target.isConnected && !this.element.contains(event.target)) {
      this.element.querySelectorAll('[data-action]').forEach(el => {
        if (el.dataset['action'].includes(`->${this.context.module.identifier}#toggle`)) {
          this._removeClickOutsideListener();
          this.hideDropdown(el);
        }
      });
    }
  }

  _setupTogglersObserverAndState() {
    this.element.querySelectorAll(`[data-action*="${this.context.module.identifier}#toggle"]`).forEach(el => {
      if (this.observer) this.observer.observe(el);
      if (el.ariaExpanded === 'true' || this.togglerIsHiddenByCss(el)) {
        this.showDropdown(el);
      } else {
        this.hideDropdown(el);
      }
    });
  }

  _setUpResponsiveObserver() {
    const controller = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        const togglerIsVisible = entries[0].isIntersecting;
        if (togglerIsVisible) {
          controller.hideDropdown(entries[0].target);
        } else {
          if (controller.togglerIsHiddenByCss(entries[0].target)) {
            controller.showDropdown(entries[0].target);
          }
        }
      },
      {root: null}
    );
  }

  _addClickOutsideListener() {
    document.addEventListener('click', this.clickOutsideListener);
  }

  _removeClickOutsideListener() {
    document.removeEventListener('click', this.clickOutsideListener);
  }

  onZoomChange() {
    this._reopenDropdown();
  }

  onResizeChange() {
    this._reopenDropdown();
  }

  onLeftPanelChange() {
    this._reopenDropdown();
  }

  _reopenDropdown() {
    if (!this.hasContentTarget || this.contentTarget.hidden) return;

    const openToggler = this.element.querySelector(`[data-action*="${this.context.module.identifier}#toggle"][aria-expanded="true"]`);
    if (!openToggler) return;

    this._removeClickOutsideListener();
    this.hideDropdown(openToggler);
    setTimeout(() => {
      this._addClickOutsideListener();
      this.showDropdown(openToggler);
    }, 10);
  }
}
